import { Helmet } from 'react-helmet-async';
import { sendGAPageview } from '../utils/gaConsent';
import SharedBadge from '../global/Badge/Badge';
import {
  collaborations,
  currentPartners,
  partnersHero,
  partnershipPillars,
  trustedBy,
} from '../data/partnersContent';
import './partners.css';
import SharedButton from '../global/Button/Button';

const Partners = () => {
  sendGAPageview({ hitType: 'pageview', page: '/partners', title: 'Partners' });

  return (
    <>
      <Helmet>
        <title>DISGENET: Partners</title>
        <meta
          name="description"
          content="Discover DISGENET strategic partnerships and collaborations in biomedical innovation."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.disgenet.com/partners" />
        <meta property="og:title" content="DISGENET: Partners" />
        <meta
          property="og:description"
          content="Discover DISGENET strategic partnerships and collaborations in biomedical innovation."
        />
      </Helmet>

      <section className="landing-surface-b border-b border-slate-200/80 py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <SharedBadge variant="secondary" size="sm" text={partnersHero.eyebrow} />
            <h6 className="mt-4 text-4xl font-bold tracking-tight dark:text-white sm:text-5xl lg:text-[3.15rem] lg:leading-tight text-blue">
              {partnersHero.titleBefore}
              <span className="text-pink">{partnersHero.titleHighlight}</span>
              {partnersHero.titleAfter}
            </h6>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600 dark:text-slate-400 sm:text-lg">
              {partnersHero.subtitle}
            </p>
          </div>
        </div>
      </section>

      <section className="landing-section landing-surface-a">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="landing-eyebrow">ORGANIZATIONS</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight dark:text-white sm:text-4xl">
              Trusted by industry leaders worldwide
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-slate-400">
              Our collaborations are built on mutual trust and shared success.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {trustedBy.map(company => (
              <article
                key={company.name}
                className="landing-card-dark brand-glow-card brand-hover-card flex items-center justify-center gap-4 border border-slate-200/70 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900/55"
              >
                <div
                  className="inline-flex h-12 w-auto shrink-0 items-center justify-center rounded-xl bg-white/95 px-3 text-sm font-bold text-primary shadow-sm dark:bg-slate-950/80 dark:shadow-[0_0_0_1px_rgba(148,163,184,0.15)]"
                  aria-hidden="true"
                >
                  <img src={company.logo} alt={company.name} className="h-6 w-auto" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-surface-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="landing-eyebrow">WHY PARTNER WITH US</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight dark:text-white sm:text-4xl">
              A collaboration model designed for long-term impact
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {partnershipPillars.map(pillar => (
              <article key={pillar.title} className="landing-card-dark brand-glow-card brand-hover-card p-6">
                <h3 className="text-lg font-semibold dark:text-white">{pillar.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {pillar.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-surface-a">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="landing-eyebrow">CURRENT PARTNERS</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight dark:text-white sm:text-4xl">
                Growing with four focused partnerships
              </h2>
            </div>
            <p className="max-w-lg text-sm text-slate-600 dark:text-slate-400">
              We currently collaborate with four key organizations and continue expanding our ecosystem with
              selective, high-value alliances.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {currentPartners.map(partner => (
              <article
                key={partner.name}
                className="landing-card-dark brand-glow-card brand-hover-card flex h-full flex-col border border-slate-200/70 p-7 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900/55"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary">
                      {partner.type}
                    </p>
                    <h3 className="mt-2 text-2xl font-bold tracking-tight dark:text-white">{partner.name}</h3>
                  </div>
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="h-10 w-auto shrink-0 bg-white text-base font-bold text-primary dark:border-white/15 dark:bg-zinc-900 dark:text-slate-100"
                    />
                  ) : (
                    <div
                      className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-base font-bold text-primary dark:border-white/15 dark:bg-zinc-900 dark:text-slate-100"
                      aria-hidden="true"
                    >
                      {partner.badge}
                    </div>
                  )}
                </div>

                <p className="mt-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {partner.description}
                </p>

                <ul className="mt-6 space-y-3">
                  {(partner.highlights ?? []).map(item => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300"
                    >
                      <span className="mt-1.5 h-2 w-2 rounded-full bg-secondary" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-8">
                  <SharedButton
                    variant="primary"
                    size="sm"
                    onClick={() => window.open(partner.website, '_blank')}
                    className="ml-0"
                  >
                    Visit website
                  </SharedButton>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-surface-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="landing-eyebrow">COLLABORATIONS</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight dark:text-white sm:text-4xl">
                Working with specialized collaborators
              </h2>
            </div>
            <p className="max-w-lg text-sm text-slate-600 dark:text-slate-400">
              We build long-term collaborations with organizations that complement our scientific and
              technology roadmap.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {collaborations.map(partner => (
              <article
                key={partner.name}
                className="landing-card-dark brand-glow-card brand-hover-card flex h-full flex-col border border-slate-200/70 p-7 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900/55"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary">
                      {partner.type}
                    </p>
                    <h3 className="mt-2 text-2xl font-bold tracking-tight dark:text-white">{partner.name}</h3>
                  </div>
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="h-10 w-auto shrink-0 bg-white text-base font-bold text-primary dark:border-white/15 dark:bg-zinc-900 dark:text-slate-100"
                    />
                  ) : (
                    <div
                      className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-base font-bold text-primary dark:border-white/15 dark:bg-zinc-900 dark:text-slate-100"
                      aria-hidden="true"
                    >
                      {partner.badge}
                    </div>
                  )}
                </div>

                <ul className="mt-6 space-y-3">
                  {(partner.highlights ?? []).map(item => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300"
                    >
                      <span className="mt-1.5 h-2 w-2 rounded-full bg-secondary" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-8">
                  <SharedButton
                    variant="primary"
                    size="sm"
                    onClick={() => window.open(partner.website, '_blank')}
                    className="ml-0"
                  >
                    Visit website
                  </SharedButton>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Partners;
